import type { FC } from 'react'

import { TripleDotLoader } from '@liveHelper/components/shared/TripleDotLoader'
import { VideoButtonRow } from '@liveHelper/components/shared/VideoButtonRow'
import { VisitorCameraPreview } from '@liveHelper/components/shared/VisitorCameraPreview/VisitorCameraPreview'
import { useAppointment } from '@liveHelper/context/AppointmentContext/AppointmentContext'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { useAppointmentActions } from '@liveHelper/hooks/useAppointmentActions'
import { useSecondsCountdown } from '@liveHelper/hooks/useSecondsCountdown'
import { useVisitorCanUseCamera } from '@liveHelper/hooks/useVisitorCanUseCamera'
import { cameraEnabledSelector, useStore } from '@liveHelper/models/store'
import { START_CALL, TERMINATE_CALL } from '@liveHelper/testIds'
import { ASPECT_RATIO_LANDSCAPE } from '@oneToOne/shared/components/chat/constants'

import { Markdown } from '@core/components/Markdown'
import { Button } from '@core/components/twind/Button'
import { useContextTranslation } from '@core/i18n'
import { useTwindContext } from '@core/twind'
import { isMobileWeb } from '@core/userAgent'

import {
  appointmentLobbyContainer,
  appointmentLobbyLegalStatement,
} from './AppointmentLobby.twind'
import videoCameraBackground from './videoCameraBackground.png'

interface Props {
  isDialing: boolean
}

const formatTime = (dateObject: Date): { time: string; date: string } => {
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  // Format the time (e.g., 9:15am)
  const timeFormatter = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })
  return {
    date: dateFormatter.format(dateObject),
    time: timeFormatter.format(dateObject),
  }
}

/**
 * Appointment Component - Lobby
 */
export const AppointmentLobby: FC<Props> = ({ isDialing }) => {
  const { t } = useContextTranslation()
  const { tw } = useTwindContext()

  const { appointment } = useAppointment()
  const { startCall, terminateCall } = useAppointmentActions()

  const chatChannelConfig = useChatChannelConfig()

  const { secondsLeft } = useSecondsCountdown(
    Date.parse(appointment?.starts_at || ''),
  )
  const isButtonActive =
    secondsLeft <= 600 &&
    (!appointment?.status || appointment?.status === 'not_started')

  const isCameraEnabled = useStore(cameraEnabledSelector)
  const visitorCanUseCamera = useVisitorCanUseCamera()
  const isMobileWebEnabled = isMobileWeb()

  return (
    <div
      className={tw?.(
        appointmentLobbyContainer(isMobileWebEnabled),
        'w-full',
        'h-full',
        'px-6',
        'py-12',
        'flex',
        'flex-col',
        'justify-between',
        'text-white',
        'overflow-y-auto',
      )}
    >
      <div>
        <div className={tw?.('text-center', 'mb-12')}>
          <p className={tw?.('text-xl', 'font-semibold')}>
            {t('oneToOne:Hi, {{name}}!', {
              name: appointment?.invitee_name,
            })}
          </p>
          <p className={tw?.('text-sm', 'whitespace-pre-line')}>
            {appointment?.status === 'expired'
              ? t('oneToOne:Your appointment has expired')
              : appointment?.status === 'canceled'
                ? t('oneToOne:Your appointment has been canceled')
                : secondsLeft === 0 || isDialing
                  ? t('oneToOne:We will be right with you')
                  : secondsLeft > 600
                    ? t(
                        `oneToOne:Get ready for your appointment on {{date}} at {{time}}`,
                        formatTime(new Date(appointment?.starts_at || '')),
                      )
                    : t(
                        'oneToOne:Get ready for your appointment in {{minutesLeft}}:{{secondsLeft}}',
                        {
                          minutesLeft: Math.floor(secondsLeft / 60),
                          secondsLeft: String(
                            Math.floor(
                              secondsLeft - Math.floor(secondsLeft / 60) * 60,
                            ),
                          ).padStart(2, '0'),
                        },
                      )}
          </p>
        </div>
        <div className={tw?.('mb-4', 'flex', 'justify-center')}>
          {isCameraEnabled && visitorCanUseCamera ? (
            <VisitorCameraPreview
              visitorName={appointment?.invitee_name || ''}
            />
          ) : (
            <img
              style={{ aspectRatio: ASPECT_RATIO_LANDSCAPE }}
              src={videoCameraBackground}
              alt=""
            />
          )}
        </div>
        <VideoButtonRow
          isVisible={true}
          isOnCall={false}
          showChatButton={false}
        />
      </div>
      <div className={tw?.('text-center')}>
        <p
          className={tw?.('mb-4', 'text-sm')}
          dangerouslySetInnerHTML={{
            __html: t?.(
              'oneToOne:<strong>Use your microphone</strong> to ensure the best possible experience while we assist you.',
              { interpolation: { escapeValue: false } },
            ),
          }}
        />
        {isDialing ? (
          <>
            <div className={tw?.('mt-6', 'mb-6')}>
              <TripleDotLoader />
            </div>
            <button
              onClick={terminateCall}
              className={tw?.('focus:outline-none', 'text')}
              data-testid={TERMINATE_CALL}
            >
              {t?.('oneToOne:Back')}
            </button>
          </>
        ) : (
          <>
            <Button
              block={true}
              aria-label={t(`oneToOne:Join call`)}
              onClick={startCall}
              className={tw?.('mb-4')}
              disabled={!isButtonActive}
              data-testid={START_CALL}
            >
              {t('oneToOne:Join call')}
            </Button>
            {chatChannelConfig?.config?.legal_statement ? (
              <div className={tw?.(appointmentLobbyLegalStatement)}>
                <Markdown>{chatChannelConfig.config.legal_statement}</Markdown>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
